
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { isEmpty } from 'src/app/helpers/transformation-utils';
import { validateBrazilianDocument } from 'src/app/helpers/documents-utils';
import {
  FilterFieldNames,
  ListFilters,
  ListFilterService,
} from '../services/list-filter.service';
import { Subscription } from 'rxjs';
import { DateFormat } from 'src/app/shared/enums/date-formats.enum';
import { DateUtilsService } from 'src/app/services/date-utils.service';

export type FilterFieldTag = {
  name: FilterFieldNames;
  value: string;
  label: string;
};

const FILTER_LABEL_MAP: Record<FilterFieldNames, string> = {
  document: $localize`:@@label_document:Documento`,
  name: $localize`:@@label_name:Nombre`,
  dateRange: $localize`:@@inclusionDate:Fecha de inclusión`,
  scoreRange: $localize`:@@label_score:Score`,
  restrictivesRange: $localize`:@@restrictiveQuantity:Cantidad de restrictivas`,
  internalCode: $localize`:@@internalCode2:Código interno`,
  segment: $localize`:@@segment:Segmento`,
};

@Component({
    selector: 'app-filter-tags',
    templateUrl: './filter-tags.component.html',
    styleUrls: ['./filter-tags.component.scss'],
    imports: [MatChipsModule, MatIconModule]
})
export class FilterTagsComponent implements OnInit, OnDestroy {
  private subscription?: Subscription;

  constructor(
    private readonly filterService: ListFilterService,
    private readonly dateUtilsService: DateUtilsService
  ) {}

  ngOnInit(): void {
    this.subscription = this.filterService.filterValues$.subscribe((values) => {
      this.fillFilterTags(values);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
  filterFieldTags: FilterFieldTag[] = [];

  fillFilterTags(values: ListFilters) {
    if (!values) {
      this.filterFieldTags = [];
      return;
    }

    const tags = (Object.keys(values) as Array<keyof ListFilters>)
      .map((key) => {
        const value = values[key];
        let empty = isEmpty(value);

        if (key === 'document' && typeof value === 'string' && value) {
          const validationResult = validateBrazilianDocument(value);
          empty = !validationResult.isValid;
        }

        return {
          name: key,
          value: this.getFilterValue(key, values),
          label: FILTER_LABEL_MAP[key],
          empty,
        };
      })
      .filter((tag) => !tag.empty);

    this.filterFieldTags = tags;
  }

  handleRemove(fieldName: FilterFieldNames) {
    this.filterService.clearFilterField(fieldName);
  }

  getTagText(tag: FilterFieldTag) {
    return tag.label + ': ' + tag.value;
  }

  getFilterValue(fieldName: FilterFieldNames, filtersValue: ListFilters) {
    const value = filtersValue[fieldName];

    if (!value) {
      return '';
    }

    switch (fieldName) {
      case 'dateRange': {
        const start = value['start']
          ? this.dateUtilsService.dateToUserTimezone(
              value['start'],
              DateFormat.DATE
            )
          : null;
        const end = value['end']
          ? this.dateUtilsService.dateToUserTimezone(
              value['end'],
              DateFormat.DATE
            )
          : null;
        return this.formatRangeFilter(start, end);
      }

      case 'scoreRange':
      case 'restrictivesRange': {
        const min = value['min'];
        const max = value['max'];
        return this.formatRangeFilter(min, max);
      }

      default:
        return typeof value === 'object'
          ? JSON.stringify(value)
          : value.toString();
    }
  }

  private formatRangeFilter(minValue: unknown, maxValue: unknown): string {
    const hasMin =
      minValue !== null && minValue !== undefined && minValue !== '';
    const hasMax =
      maxValue !== null && maxValue !== undefined && maxValue !== '';

    if (hasMin && hasMax) {
      return $localize`:@@between:Entre ${minValue} y ${maxValue}`;
    }
    if (hasMin) {
      return $localize`:@@greatherThan:Mayor que ${minValue}`;
    }
    if (hasMax) {
      return $localize`:@@lessThan:Menor que ${maxValue}`;
    }
    return '';
  }
}
